import React, { useCallback, useEffect, useState } from 'react';
import { Button, Form, Input, Modal, Typography, message } from 'antd';
import { EyeInvisibleOutlined, EyeTwoTone, UnlockOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';

import { useAppDispatch, useAppSelector } from '../Modules/Presentation/hooks';
import { selectUser } from '../Modules/User/Presentation/selectors';
import {
  activateSlotOpsOverrideThunk,
  clearExpiredSlotOpsOverrideThunk,
} from '../Modules/User/Presentation/actions';
import { canUseSlotOpsOverride } from '../Utils/slotOpsOverride';
import { isSlotOperationRestricted } from '../Utils/timeRestriction';

dayjs.extend(utc);
dayjs.extend(timezone);

const { Text } = Typography;

const barStyle = (active: boolean): React.CSSProperties => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 8,
  padding: '4px 10px',
  marginBottom: 8,
  fontSize: 12,
  lineHeight: 1.4,
  color: active ? '#389e0d' : '#8c8c8c',
  background: active ? '#f6ffed' : '#fafafa',
  border: `1px solid ${active ? '#b7eb8f' : '#f0f0f0'}`,
  borderRadius: 4,
});

const formatCountdown = (totalSeconds: number): string => {
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
};

const getRemainingSeconds = (expiresAt: string): number => {
  const remainingMs = dayjs(expiresAt).valueOf() - Date.now();
  return Math.max(0, Math.ceil(remainingMs / 1000));
};

const SlotOpsOverrideBanner: React.FC = () => {
  const dispatch = useAppDispatch();
  const user = useAppSelector(selectUser);
  const [modalOpen, setModalOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form] = Form.useForm<{ password: string }>();
  const [, setTick] = useState(0);

  const override = user?.slot_ops_override;
  const isActive = Boolean(override?.active);
  const expiresAt = override?.expires_at ?? null;
  const isRestricted = user ? isSlotOperationRestricted(user) : false;

  useEffect(() => {
    if (!isActive || !expiresAt) return undefined;

    const expiryMs = dayjs(expiresAt).valueOf();
    const timer = window.setInterval(() => {
      if (Date.now() >= expiryMs) {
        dispatch(clearExpiredSlotOpsOverrideThunk());
        message.info('슬롯 임시 해제가 만료되었습니다.');
        return;
      }
      setTick(t => t + 1);
    }, 1000);

    return () => window.clearInterval(timer);
  }, [dispatch, expiresAt, isActive]);

  const handleOpenModal = useCallback(() => {
    form.resetFields();
    setModalOpen(true);
  }, [form]);

  const handleCloseModal = useCallback(() => {
    form.resetFields();
    setModalOpen(false);
  }, [form]);

  const handleSubmit = useCallback(async () => {
    try {
      const { password } = await form.validateFields();
      setSubmitting(true);
      await dispatch(activateSlotOpsOverrideThunk(password)).unwrap();
      message.success('15분 동안 슬롯 작업 제한이 임시 해제되었습니다.');
      handleCloseModal();
    } catch (error) {
      message.error(typeof error === 'string' ? error : '임시 해제에 실패했습니다.');
    } finally {
      setSubmitting(false);
    }
  }, [dispatch, form, handleCloseModal]);

  if (!canUseSlotOpsOverride(user)) {
    return null;
  }

  if (isActive && expiresAt) {
    const remainingSeconds = getRemainingSeconds(expiresAt);

    return (
      <div style={{ ...barStyle(true), marginTop: 12 }}>
        <Text style={{ fontSize: 12, color: 'inherit' }}>
          임시 해제 중 · {formatCountdown(remainingSeconds)} 남음
        </Text>
      </div>
    );
  }

  if (!isRestricted) {
    return null;
  }

  return (
    <>
      <div style={{ ...barStyle(false), marginTop: 12 }}>
        <Text style={{ fontSize: 12, color: 'inherit' }}>
          현재 시간대 슬롯 작업 제한
        </Text>
        <Button
          type="link"
          size="small"
          icon={<UnlockOutlined />}
          onClick={handleOpenModal}
          style={{ fontSize: 12, height: 'auto', padding: 0 }}
        >
          15분 임시 해제
        </Button>
      </div>

      <Modal
        title="슬롯 작업 임시 해제 (15분)"
        open={modalOpen}
        onCancel={handleCloseModal}
        destroyOnClose
        footer={[
          <Button key="cancel" onClick={handleCloseModal}>
            취소
          </Button>,
          <Button
            key="submit"
            type="primary"
            loading={submitting}
            onClick={handleSubmit}
          >
            해제 시작
          </Button>,
        ]}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            label="슈퍼어드민 비밀번호"
            name="password"
            rules={[{ required: true, message: '비밀번호를 입력해 주세요.' }]}
          >
            <Input.Password
              placeholder="전달받은 비밀번호 입력"
              autoComplete="off"
              iconRender={visible => (visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />)}
            />
          </Form.Item>
        </Form>
        <Text type="secondary">
          성공 시 현재 브라우저에서 15분간 슬롯 시간 제한이 해제됩니다.
        </Text>
      </Modal>
    </>
  );
};

export default SlotOpsOverrideBanner;
